import { z } from 'zod';
import { dateSchema, integerSchema, objectIdSchema, quantitySchema } from '../../validation';
import { billingUnitSchema } from '../../common/app-registry.schema';

/**
 * How a subscription's right to act expires.
 *
 * A Subscription is a grant of permission to use the platform. The two
 * models differ only in what ends that grant:
 *
 * - `period` — it expires on a **date**. The org commits to a quantity
 *   per month, pays per interval, renews, can fall behind (dunning),
 *   and is billed overage when it exceeds the commitment.
 * - `usage` — it expires at a **count**. The org buys N units up front
 *   at a volume rate, spends them one at a time, and buys more when
 *   they run out. There is no interval, no renewal, no dunning and no
 *   overage — running out *is* the renewal prompt.
 *
 * Everything else is shared: one per org, bought through the same
 * `Billing` → Moyasar rail, granting the same modules, answering the
 * same question at the entitlement gate.
 *
 * Absent means `period`, so every subscription written before this
 * field existed keeps its behaviour with no migration.
 */
export const subscriptionModelSchema = z.enum(['period', 'usage']);
export type SubscriptionModel = z.infer<typeof subscriptionModelSchema>;

/** Model assumed for subscriptions that predate the field. */
export const DEFAULT_SUBSCRIPTION_MODEL: SubscriptionModel = 'period';

/**
 * What a `usage` subscription meters — extract of {@link billingUnitSchema}.
 *
 * Narrowed to `order` because that is the only thing a prepaid bucket
 * spends — `vehicle` is a monthly seat commitment on a `period`
 * subscription (`purchase.unit`), which has no balance to debit.
 *
 * Prefer `Subscription.purchase.unit` for new reads; this type remains
 * for the balance meter vocabulary.
 */
export const subscriptionUsageUnitSchema = billingUnitSchema.extract(['order']);
export type SubscriptionUsageUnit = z.infer<typeof subscriptionUsageUnitSchema>;

/**
 * How a granted bucket of credits behaves against future purchases.
 *
 * - `free` — a true gift. It adds usable credits (`balance.granted` and
 *   `balance.remaining` both go up) and is never charged back. Spent
 *   before paid credits, and reclaimable while unspent (expiry, revoke,
 *   the global zero).
 * - `repayable` — the classic "try it, then pay it back". It does NOT
 *   add usable credits; it widens the overdraft floor so `remaining`
 *   may run negative by its `qty`, and the next purchase settles that
 *   debt through the same `$inc` that adds the bought credits. This is
 *   the generalised, per-grant form of the old fixed `freeAllowance`.
 */
export const subscriptionGrantModeSchema = z.enum(['free', 'repayable']);
export type SubscriptionGrantMode = z.infer<typeof subscriptionGrantModeSchema>;

/**
 * One bucket of credits handed to an owner outside a purchase — a
 * signup allowance or an admin gift.
 *
 * Grants are the **ledger for gifted credits**, the mirror of the
 * capture markers that are the ledger for consumption. `balance.granted`
 * is the cache of the unspent `free` grants, rebuildable from this array
 * plus the markers (each marker carries the `grantId` it drew from).
 *
 * `expiresAt` is optional on purpose: set it for a time-limited promo,
 * omit it for an open-ended grant that lives until the credits are
 * spent. Only grants that carry an `expiresAt` are ever touched by the
 * expiry sweep.
 */
export const subscriptionGrantSchema = z.object({
  /** Stable id, referenced by capture markers that spent from it. */
  _id: objectIdSchema,
  /** Credits granted by this bucket. */
  qty: quantitySchema,
  /**
   * Units of this grant already spent — a cache of the capture markers
   * that carry this grant's `_id`. Lets a capture pick a grant with
   * spare capacity in one write, and expiry/revoke reclaim only the
   * unspent `qty − consumed`. Rebuilt from the markers by
   * `SubscriptionCreditsService.reconcile`. Only meaningful for `free`
   * grants (repayable grants are never marked).
   */
  consumed: quantitySchema.default(0),
  /** `free` gift vs `repayable` overdraft — see {@link subscriptionGrantModeSchema}. */
  mode: subscriptionGrantModeSchema,
  /** Free-text audit note ("launch promo", "goodwill for outage"). */
  reason: z.string().optional(),
  /** Admin user who created the grant, when it was a manual gift. */
  grantedBy: objectIdSchema.optional(),
  grantedAt: dateSchema,
  /** When the grant lapses. Absent = never expires by time; lives until spent. */
  expiresAt: dateSchema.optional(),
  /** Set when an operator (or the global zero) pulled the grant by hand. */
  revokedAt: dateSchema.optional(),
  /** Set by the expiry sweep once `expiresAt` has passed. */
  expiredAt: dateSchema.optional(),
  /**
   * A free period instead of a count: every order is free until
   * `expiresAt`, and `qty` is `0`. `expiresAt` is unset until the clock
   * starts — at the first waybill, or already at signup. Orders it covers
   * are marked with its `_id` like any free spend, so `consumed` counts
   * them and `reconcile` reads them as free.
   */
  window: z
    .object({
      days: z.number().int().positive(),
      startsOn: z.enum(['first_waybill', 'signup']),
    })
    .optional(),
});
export type SubscriptionGrant = z.infer<typeof subscriptionGrantSchema>;

/**
 * The credit bucket on a `usage` subscription.
 *
 * **This is a cache, not the ledger.** The authoritative record of
 * consumption is the set of capture markers on the orders themselves
 * (`Order.subscriptions[]`), and `remaining` is always recomputable as
 * `purchased + granted − count(orders captured by this subscription)`.
 *
 * That matters because a capture is two writes to two documents — the
 * counter here and the marker on the Order — with no transaction
 * between them. Treating the markers as truth makes any drift
 * repairable by counting (see
 * `SubscriptionCreditsService.reconcile`) instead of unrecoverable.
 * Code that needs a number fast reads `remaining`; code that needs to
 * be *right* counts markers.
 *
 * `consumed` is kept alongside rather than derived from
 * `purchased − remaining` so a reconciliation can tell the difference
 * between "we lost a decrement" and "we lost a purchase".
 */
export const subscriptionBalanceSchema = z.object({
  /** Total units ever bought, across every top-up. Only ever grows. */
  purchased: quantitySchema,
  /** Units spent and not reversed. */
  consumed: quantitySchema,
  /**
   * Unspent `free` gifted credits currently counted into `remaining`.
   *
   * The cache of the `free` entries in {@link grants} that have not been
   * spent, expired, or revoked. Rebuilt by
   * `SubscriptionCreditsService.reconcile` from the grants array minus
   * the markers that drew from them. `repayable` grants do NOT feed this
   * — they widen the overdraft floor instead.
   */
  granted: quantitySchema.default(0),
  /**
   * Paid units written off unspent — the bucket is zeroed when the owner
   * moves to an unlimited plan on the same product. Only ever grows, and
   * comes off `remaining` exactly like a spend, so `reconcile` keeps the
   * bucket at zero instead of handing the credits back.
   */
  forfeited: quantitySchema.default(0),
  /**
   * `purchased + granted − consumed − forfeited`, cached for the entitlement gate.
   *
   * **Signed**, unlike `purchased`/`consumed`/`granted`. A `repayable`
   * grant (the generalised free allowance) is carried here as debt —
   * down to `-(sum of active repayable grants)` — so the next purchase
   * settles it through the same `$inc` that adds the bought credits (see
   * `SubscriptionCreditsService`).
   *
   * A non-negative schema here is not a safety net, it is a bug: the
   * `$inc` that spends a repayable grant bypasses validation, so the doc
   * lands at `-1` regardless and the *next* validated write to it — a
   * top-up, a plan change — is what fails, telling the buyer that
   * "remaining is below the minimum" at the moment they were trying to
   * pay us.
   */
  remaining: integerSchema,
  /**
   * Gifted credit buckets — signup allowances and admin gifts. The
   * ledger for gifts; `granted` is its cache. See
   * {@link subscriptionGrantSchema}.
   */
  grants: z.array(subscriptionGrantSchema).default([]),
});
export type SubscriptionBalance = z.infer<typeof subscriptionBalanceSchema>;
